import React, { useState, useEffect, useCallback } from 'react';
import { useHistory, Link } from 'react-router-dom';
import { Button, Table, FormGroup, Spinner } from 'reactstrap';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync } from '@fortawesome/free-solid-svg-icons';
import footballService from '../../services/api/football-service.js';

const StatusBadge = ({ status }) => {
    const styles = {
        completed: 'bg-dark text-white',
        not_started: 'bg-secondary text-white',
        live: 'bg-success text-white',
    };
    const defaultStyle = 'bg-warning text-dark';
    return (
        <span className={`badge rounded-pill fw-bold ${styles[status] || defaultStyle}`}>
            {status?.replace(/_/g, ' ').toUpperCase() || 'N/A'}
        </span>
    );
};

function LiveMatchesPage() {
    const [allLiveMatches, setAllLiveMatches] = useState([]);
    const [displayedMatches, setDisplayedMatches] = useState([]);
    const [tournaments, setTournaments] = useState([]);
    const [filterSlug, setFilterSlug] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const history = useHistory();

   const fetchInitialData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [matchesData, tournamentsData] = await Promise.all([
                footballService.getLiveMatches(),
                footballService.getTournaments({ size: 100 }) 
            ]);

            setAllLiveMatches(matchesData.data);
            setDisplayedMatches(matchesData.data);
            
            if (tournamentsData.data) {
                setTournaments(tournamentsData.data);
            }

        } catch (err) {
            const errorMessage = err.response?.data?.error || err.message || 'Failed to fetch initial data.';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchInitialData();
    }, [fetchInitialData]);

    useEffect(() => {
        // CHANGED: Filtering logic now uses tournament slug (t_slug)
        if (filterSlug === '') {
            setDisplayedMatches(allLiveMatches);
        } else {
            const filtered = allLiveMatches.filter(
                ({ match }) => match.tournament.t_slug === filterSlug
            );
            setDisplayedMatches(filtered);
        }
    }, [filterSlug, allLiveMatches]);
    
    // CHANGED: Handler now accepts a slug
    const handleRowClick = (slug) => history.replace(`/dashboard/football/match/${slug}`);

    const handleRefreshClick = (e) => {
        e.stopPropagation();
        fetchInitialData();
    };

    return (
        <>
            <div className="p-4 d-flex flex-column flex-md-row align-items-center justify-content-between border-bottom">
                <FormGroup className="mb-md-0 me-md-auto" style={{ minWidth: '300px' }}>
                    <Select
                        placeholder="Filter by Tournament"
                        options={tournaments.map(tournament => ({ label: tournament.name, value: tournament.t_slug }))}
                        value={tournaments.find(t => t.t_slug === filterSlug) ? { label: tournaments.find(t => t.t_slug === filterSlug).name, value: filterSlug } : null}
                        onChange={(selectedOption) => { setFilterSlug(selectedOption ? selectedOption.value : ''); }}
                        isClearable
                        classNamePrefix="react-select"
                    />
                </FormGroup>
                <div className="d-flex align-items-center gap-2 mt-3 mt-md-0">
                    <Button
                        className="btn-round px-4 py-2"
                        color="success"
                        type="button"
                        onClick={handleRefreshClick}
                        disabled={loading}
                    >
                        <FontAwesomeIcon icon={faSync} className={loading ? "fa-spin" : ""} />
                        <span className="ms-2 d-none d-sm-inline">{loading ? 'Refreshing...' : 'Refresh'}</span>
                    </Button>
                </div>
            </div>

            <div className="table-responsive">
                <Table className="table-hover table-striped mb-0">
                    <thead className="text-dark bg-light text-uppercase small">
                        <tr>
                            <th className="text-left py-3 px-4">Match</th>
                            <th className="text-center py-3 px-4">Score</th>
                            <th className="text-left py-3 px-4 d-none d-sm-table-cell">Tournament</th>
                            <th className="text-center py-3 px-4">Status</th>
                            <th className="text-center py-3 px-4">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan="5" className="text-center py-5">
                                    <Spinner animation="border" role="status" className="text-success" />
                                    <p className="mt-2 text-muted">Loading live matches...</p>
                                </td>
                            </tr>
                        ) : error ? (
                            <tr>
                                <td colSpan="5" className="text-center text-danger py-5">{error}</td>
                            </tr>
                        ) : displayedMatches.length > 0 ? (
                            displayedMatches.map(({ match, score }) => (
                                <tr key={match.m_slug} onClick={() => handleRowClick(match.m_slug)} style={{ cursor: 'pointer' }} className="align-middle">
                                    <td className="text-left px-4">{match.title}</td>
                                    <td className="text-center px-4">
                                        <span className="fw-bold text-success fs-5">{score ? `${score.home} - ${score.away}` : '0 - 0'}</span>
                                    </td>
                                    <td className="text-left px-4 d-none d-sm-table-cell">{match.tournament.name}</td>
                                    <td className="text-center px-4">
                                        <StatusBadge status={match.status} />
                                    </td>
                                    <td className="text-center px-4">
                                        <Link
                                          to={`/dashboard/football/match/${match.m_slug}`}
                                          className="btn btn-success btn-sm"
                                        >
                                          Details
                                        </Link>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="5" className="text-center py-5">
                                    <h4 className="text-muted">
                                        {filterSlug ? 'No live matches for this tournament.' : 'There are no active live matches at the moment.'}
                                    </h4>
                                    <p className="text-muted">Try a different filter or check back later.</p>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>
        </>
    );
}

export default LiveMatchesPage;